import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crop, Loader2, RotateCcw, Sparkles } from "lucide-react";
import { computeFraming, DEFAULT_FRAMING } from "@/lib/coverFraming";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Smart Full-Subject Cover: ALWAYS a filled object-fit: cover crop. The analysis only picks the focal point
// (feet/lower body kept, cropped from the top when the person is taller than the card window) and zoom.
export const SmartCover = ({ src, frame, aspect = 1, className = "", imgClassName = "", alt = "", testid, onImgLoad }) => {
  const f = frame?.raw ? computeFraming(frame.raw, aspect) : (frame?.mode ? frame : DEFAULT_FRAMING);
  if (!src) return null;
  return (
    <div className={`relative w-full h-full overflow-hidden bg-black ${className}`} data-testid={testid} data-cover-mode="cover" data-cover-pos={`${f.x},${f.y}`} data-cover-zoom={f.zoom}>
      <img src={src} alt={alt} draggable={false} onLoad={onImgLoad} className={`w-full h-full object-cover select-none pointer-events-none ${imgClassName}`}
        style={{ objectPosition: `${f.x}% ${f.y}%`, ...(f.zoom > 1 ? { transform: `scale(${f.zoom})`, transformOrigin: `${f.x}% ${f.y}%` } : {}) }} />
    </div>
  );
};

// Click-and-drag repositioning of an object-fit: cover image. Dragging moves the image under the fixed crop window by
// converting pixel deltas into object-position % (the SAME focal x/y metadata the old sliders edited), clamped 0–100 so the
// crop can never show empty space. Zoom (transform scale about the focal point) keeps the crop filled too.
const DragCover = ({ src, x, y, zoom, aspect, onMove, testid }) => {
  const boxRef = useRef(null);
  const natRef = useRef({ w: 0, h: 0 });
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const onImgLoad = (e) => { natRef.current = { w: e.target.naturalWidth, h: e.target.naturalHeight }; };
  const overflow = () => {
    const box = boxRef.current?.getBoundingClientRect(); const { w, h } = natRef.current;
    if (!box || !w || !h) return { ox: 0, oy: 0 };
    const s = Math.max(box.width / w, box.height / h);                 // object-fit: cover scale
    return { ox: Math.max(0, w * s - box.width), oy: Math.max(0, h * s - box.height) };
  };
  const down = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { px: e.clientX, py: e.clientY, x, y, ...overflow() };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = dragRef.current; if (!d) return;
    e.preventDefault();
    const dx = e.clientX - d.px, dy = e.clientY - d.py;
    // moving the image right/down shows more of its left/top → position % decreases; zoom magnifies on-screen motion
    const nx = d.ox > 0 ? d.x - (dx / (d.ox * zoom)) * 100 : d.x;
    const ny = d.oy > 0 ? d.y - (dy / (d.oy * zoom)) * 100 : d.y;
    onMove(Math.round(Math.min(100, Math.max(0, nx))), Math.round(Math.min(100, Math.max(0, ny))));
  };
  const up = () => { dragRef.current = null; setDragging(false); };
  return (
    <div ref={boxRef} data-testid={testid} data-dragging={dragging ? "true" : "false"}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
      className={`rounded-xl overflow-hidden border border-border bg-black select-none ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
      style={{ aspectRatio: String(aspect), touchAction: "none" }}>
      <SmartCover src={src} frame={{ raw: null, mode: "cover", x, y, zoom, source: "preview" }} aspect={aspect} testid="cover-adjust-preview" onImgLoad={onImgLoad} />
    </div>
  );
};

// Admin-only "Adjust Cover" — manual override stored as metadata (never edits the original image).
export const CoverAdjustDialog = ({ mediaId, src, aspect = 1, open, onOpenChange, onSaved }) => {
  const [data, setData] = useState(null);
    const [x, setX] = useState(50); const [y, setY] = useState(50); const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !mediaId) return;
    setData(null);
    api.get(`/media/${mediaId}/cover-frame`).then((r) => {
      setData(r.data);
      const f = computeFraming(r.data.raw, aspect);
      setX(f.x); setY(f.y); setZoom(f.zoom);
    }).catch(() => toast.error("Could not load cover framing"));
  }, [open, mediaId, aspect]);
  const smart = data ? computeFraming({ ...data.raw, manual: null }, aspect) : DEFAULT_FRAMING;
  const save = async () => {
    setBusy(true);
    try { const r = await api.patch(`/media/${mediaId}/cover-frame`, { x, y, zoom }); toast.success("Cover framing saved"); onSaved?.(r.data); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/media/${mediaId}/cover-frame/manual`);
      const f = computeFraming(r.data.raw, aspect); setX(f.x); setY(f.y); setZoom(f.zoom); setData((d) => ({ ...d, ...r.data }));
      toast.success("Back to Smart Framing"); onSaved?.(r.data);
    } catch { toast.error("Could not reset"); }
    setBusy(false);
  };
  const analyze = async () => {
    setBusy(true);
    try { const r = await api.post(`/media/${mediaId}/cover-frame/analyze`); setData((d) => ({ ...d, ...r.data })); const f = computeFraming(r.data.raw, aspect); setX(f.x); setY(f.y); setZoom(f.zoom); toast.success("Re-analyzed subject bounds"); onSaved?.(r.data); }
    catch { toast.error("Analysis failed"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="cover-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crop className="w-4 h-4 text-primary" /> Adjust cover framing</DialogTitle>
          <DialogDescription>Drag the photo to reposition it and use Zoom to tighten the crop — always an edge-to-edge cover, exactly as the gallery card shows it. Stored as metadata, the original file is never modified.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <DragCover src={src} x={x} y={y} zoom={zoom} aspect={aspect} testid="cover-drag-area" onMove={(nx, ny) => { setX(nx); setY(ny); }} />
              <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid="cover-drag-hint">Drag the photo to reposition it under the {aspect === 0.75 ? "3:4 card" : "card"} crop · <span className="font-mono">{x}% / {y}%</span></div>
              <div className="mt-2 text-[11px] text-muted-foreground" data-testid="cover-adjust-smart-info">
                Smart framing: <span className="text-foreground">focal point {smart.x}% / {smart.y}%</span>
                {data.raw?.analyzed ? "" : " · not analyzed yet"}
                {data.raw?.manual ? <span className="text-amber-300"> · manual override active</span> : null}
              </div>
            </div>
            <div className="space-y-4 text-sm">
              <label className="block">
                <div className="flex justify-between text-xs mb-1"><span>Zoom</span><span className="font-mono text-muted-foreground">{zoom.toFixed(2)}×</span></div>
                <input data-testid="cover-zoom" type="range" min="1" max="1.6" step="0.02" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-primary" />
              </label>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" data-testid="cover-save" onClick={save} disabled={busy} className="rounded-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save framing"}</Button>
                <Button size="sm" variant="outline" data-testid="cover-reset" onClick={reset} disabled={busy} className="rounded-full"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Smart Framing</Button>
                <Button size="sm" variant="ghost" data-testid="cover-analyze" onClick={analyze} disabled={busy} className="rounded-full text-muted-foreground"><Sparkles className="w-3.5 h-3.5 mr-1" /> Re-analyze</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Small overlay button for admins on any gallery tile/card.
export const CoverAdjustButton = ({ mediaId, src, aspect = 1, onSaved, className = "" }) => {
  const [open, setOpen] = useState(false);
  if (!mediaId) return null;
  return (
    <>
      <button type="button" data-testid={`cover-adjust-${mediaId}`} title="Adjust cover framing (admin)"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`absolute top-1.5 right-1.5 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/60 backdrop-blur border border-white/15 text-white/80 hover:text-primary hover:border-primary/60 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity ${className}`}>
        <Crop className="w-3.5 h-3.5" />
      </button>
      {open && <CoverAdjustDialog mediaId={mediaId} src={src} aspect={aspect} open={open} onOpenChange={setOpen} onSaved={onSaved} />}
    </>
  );
};
